/**
 * EVERY PASSWORD LEAVES THE BROWSER ENCRYPTED, AND ONLY HERE.
 *
 * The backend's rule (Rachel, 22-09-2026, narrowed 24-09-2026), not yet written
 * into `openapi/pharmos-api.yaml`:
 *
 *   plaintext  = the password as typed. The LOGIN password only has
 *                `<seconds since 1970-01-01 UTC>` straight in front, nothing
 *                between: `1790060767hunter2abc`. Whole seconds, 10 digits
 *                until the year 2286, so the server takes the first 10
 *                characters of a login password as the time and the rest as
 *                the password. A reset's new password, and both fields of a change,
 *                are the password alone — a stamp there would be read as the
 *                start of the password.
 *   ciphertext = RSA/ECB/OAEPWithSHA-1AndMGF1Padding under the API host's public key
 *   on the wire, base64 of the ciphertext, in the field the password was in
 *
 * The timestamp is what lets the server refuse a captured LOGIN body played
 * back later. OAEP is randomised anyway, so the timestamp is the freshness
 * proof, not the variety. The other password fields have no freshness proof.
 *
 * Every password field the contract has goes through it: `login.password`
 * (stamped), `reset-password.newPassword`, and both fields of
 * `PUT /v1/user/password` (those three unstamped). `src/lib/auth/api.ts` is
 * the only caller.
 *
 * WEB CRYPTO, NOT A LIBRARY. Java's `OAEPWithSHA-1AndMGF1Padding` is OAEP with
 * SHA-1 for both the label hash and MGF1, which is exactly Web Crypto's
 * `{ name: "RSA-OAEP", hash: "SHA-1" }`. `crypto.subtle` exists only in a
 * secure context (HTTPS, or localhost), so a terminal opened over plain HTTP
 * cannot sign in, and says so rather than sending a password in the clear.
 */
import { apiMode, passwordPublicKey } from "./config";
import { AppError } from "./problems";

/*
 * THE KEY IS THE BUILD'S, NOT THIS FILE'S. Each API host has its own key pair,
 * so the public half arrives as `VITE_PASSWORD_PUBLIC_KEY` (`api/config.ts`)
 * beside the base URL it belongs to. The backend team supplies the matching
 * key for each host. A build that names an API host without a key is refused
 * by `vite.config.ts`; one that names no host (the CI build) refuses to sign
 * in instead, saying why.
 *
 * A MOCK BUILD with no key makes a throwaway pair in the page: the mock holds
 * no private key and cannot read the password anyway, so any key will do, and
 * the real encryption still runs on every mocked sign-in.
 */

const OAEP_SHA1 = { name: "RSA-OAEP", hash: "SHA-1" } as const;

function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Imported once per key: the same key is used for every password this page sends. */
const imported = new Map<string, Promise<CryptoKey>>();

function importKey(spki: string): Promise<CryptoKey> {
  let key = imported.get(spki);
  if (!key) {
    key = crypto.subtle.importKey("spki", base64ToBytes(spki), OAEP_SHA1, false, ["encrypt"]);
    // A key that failed to import is not remembered, or one bad moment would be permanent.
    key.catch(() => imported.delete(spki));
    imported.set(spki, key);
  }
  return key;
}

let throwaway: Promise<CryptoKey> | undefined;

/** The key this build encrypts under — see the note at the top. */
function buildKey(): Promise<CryptoKey> {
  const spki = passwordPublicKey();
  if (spki) return importKey(spki);
  if (apiMode() === "mock") {
    throwaway ??= crypto.subtle
      .generateKey({ ...OAEP_SHA1, modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]) }, false, [
        "encrypt",
        "decrypt",
      ])
      .then((pair) => pair.publicKey);
    return throwaway;
  }
  throw new AppError("failure", {
    message: "This build has no password key (VITE_PASSWORD_PUBLIC_KEY), so it cannot sign in",
  });
}

export interface EncryptPasswordOptions {
  /**
   * The login password only. Whole seconds since 1970-01-01 UTC, written in
   * front of the password with nothing between. Every other password field
   * leaves this out, and the ciphertext is the password alone.
   */
  stamp?: Date;
  /** A test's public key. Left out, the build's key is used. */
  spki?: string;
}

/**
 * The password as the wire carries it. `stamp` is the login password's clock;
 * left out, the ciphertext is the password alone. `spki` is a test's key;
 * left out, the build's key is used.
 */
export async function encryptPassword(
  password: string,
  options: EncryptPasswordOptions = {},
): Promise<string> {
  // The DOM types promise `subtle`; an insecure page does not have it.
  if (!(crypto as Partial<Crypto>).subtle) {
    throw new AppError("failure", {
      message: "Passwords can only be sent from a secure (HTTPS) page",
    });
  }
  let plain = password;
  if (options.stamp) {
    // Epoch seconds carry no zone, so the terminal's zone cannot move them.
    const seconds = Math.floor(options.stamp.getTime() / 1000);
    if (!Number.isFinite(seconds) || seconds < 0) {
      throw new AppError("failure", { message: "The clock gave no usable time" });
    }
    plain = `${seconds}${password}`;
  }
  const plaintext = new TextEncoder().encode(plain);
  const key = await (options.spki ? importKey(options.spki) : buildKey());
  const ciphertext = await crypto.subtle.encrypt(OAEP_SHA1, key, plaintext);
  return bytesToBase64(new Uint8Array(ciphertext));
}
